import { useState, useCallback } from 'react';
import SmartTemplateBuilder, {
    SmartTemplate,
    FeeManagementTemplates,
    AerodromeTemplates,
    GovernanceTemplates,
    TreasuryTemplates
} from '../components/transaction-builder/SmartTemplateBuilder';
import logger from '../utils/logger';

// Hook for managing smart templates and launching function builder
export const useSmartTemplates = (onNavigate?: (path: string) => void) => {
    const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Create and save a smart template
    const createTemplate = useCallback(async (template: SmartTemplate): Promise<void> => {
        setIsCreatingTemplate(true);
        setError(null);

        try {
            SmartTemplateBuilder.createAndSaveTemplate(template);
            logger.log('Smart template created successfully:', template.action);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create template';
            setError(errorMessage);
            logger.error('Error creating smart template:', err);
            throw err;
        } finally {
            setIsCreatingTemplate(false);
        }
    }, []);

    // Launch function builder with a smart template
    const launchFunctionBuilder = useCallback((template: SmartTemplate, redirectPath: string = '/transaction-builder') => {
        try {
            // Convert smart template to function builder format
            const launchData = SmartTemplateBuilder.generateFunctionBuilderLaunchData(template);

            // Store the launch data in sessionStorage for the function builder to pick up
            sessionStorage.setItem('functionBuilderLaunchData', JSON.stringify({
                ...launchData,
                smartTemplate: template,
                timestamp: Date.now()
            }));

            // Navigate to the transaction builder using the callback if provided
            if (onNavigate) {
                onNavigate(redirectPath);
            } else {
                // Fallback: just log that navigation is needed
                logger.log('Navigation needed to:', redirectPath);
                logger.log('Template data stored in sessionStorage for pickup');
            }

            logger.log('Launching function builder with template:', template.action);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to launch function builder';
            setError(errorMessage);
            logger.error('Error launching function builder:', err);
        }
    }, [onNavigate]);

    // Fee Management Actions
    const createCollectAllFeesTemplate = useCallback(async (
        contractAddress: string,
        estimatedGas?: string,
        feeMetrics?: {
            unclaimedFeesETH: string;
            estimatedGasCost: string;
            profitAfterGas: string;
            isCollectionViable: boolean;
        }
    ) => {
        const template = FeeManagementTemplates.generateCollectAllFeesTemplate(
            contractAddress,
            estimatedGas,
            feeMetrics
        );

        await createTemplate(template);
        return template;
    }, [createTemplate]);

    const createCollectAndReinvestTemplate = useCallback(async (
        contractAddress: string,
        tokenId: string,
        minToken0?: string,
        minToken1?: string,
        estimatedGas?: string
    ) => {
        const template = FeeManagementTemplates.generateCollectAndReinvestTemplate(
            contractAddress,
            tokenId,
            minToken0,
            minToken1,
            estimatedGas
        );

        await createTemplate(template);
        return template;
    }, [createTemplate]);

    const createEnableAutoCompoundTemplate = useCallback(async (
        contractAddress: string,
        tokenId: string,
        minFeeThreshold: string,
        gasMultiplier: string,
        minInterval: string
    ) => {
        const template = FeeManagementTemplates.generateEnableAutoCompoundTemplate(
            contractAddress,
            tokenId,
            minFeeThreshold,
            gasMultiplier,
            minInterval
        );

        await createTemplate(template);
        return template;
    }, [createTemplate]);

    // Aerodrome Actions
    const createCollectPoolFeesTemplate = useCallback(async (
        contractAddress: string,
        poolAddress: string,
        estimatedGas?: string
    ) => {
        const template = AerodromeTemplates.generateCollectPoolFeesTemplate(
            contractAddress,
            poolAddress,
            estimatedGas
        );

        await createTemplate(template);
        return template;
    }, [createTemplate]);

    const createCollectAndReinvestPoolTemplate = useCallback(async (
        contractAddress: string,
        poolAddress: string,
        min0: string,
        min1: string,
        estimatedGas?: string
    ) => {
        const template = AerodromeTemplates.generateCollectAndReinvestPoolTemplate(
            contractAddress,
            poolAddress,
            min0,
            min1,
            estimatedGas
        );

        await createTemplate(template);
        return template;
    }, [createTemplate]);

    // Governance Actions
    const createProposalTemplate = useCallback(async (
        contractAddress: string,
        targets: string[],
        values: string[],
        calldatas: string[],
        description: string
    ) => {
        const template = GovernanceTemplates.generateCreateProposalTemplate(
            contractAddress,
            targets,
            values,
            calldatas,
            description
        );

        await createTemplate(template);
        return template;
    }, [createTemplate]);

    // Treasury Actions
    const createTokenTransferTemplate = useCallback(async (
        contractAddress: string,
        to: string,
        amount: string,
        description?: string
    ) => {
        const template = TreasuryTemplates.generateTokenTransferTemplate(
            contractAddress,
            to,
            amount,
            description
        );

        await createTemplate(template);
        return template;
    }, [createTemplate]);

    // Combined action: Create template and launch function builder
    const createAndLaunchTemplate = useCallback(async (
        template: SmartTemplate,
        redirectPath?: string
    ) => {
        await createTemplate(template);
        launchFunctionBuilder(template, redirectPath);
    }, [createTemplate, launchFunctionBuilder]);

    // Convenience methods for common workflows
    const collectAllFeesWorkflow = useCallback(async (
        contractAddress: string,
        feeMetrics?: {
            unclaimedFeesETH: string;
            estimatedGasCost: string;
            profitAfterGas: string;
            isCollectionViable: boolean;
        },
        estimatedGas?: string
    ) => {
        const template = FeeManagementTemplates.generateCollectAllFeesTemplate(
            contractAddress,
            estimatedGas,
            feeMetrics
        );

        await createAndLaunchTemplate(template);
    }, [createAndLaunchTemplate]);

    const collectAndReinvestWorkflow = useCallback(async (
        contractAddress: string,
        tokenId: string,
        minToken0?: string,
        minToken1?: string,
        estimatedGas?: string
    ) => {
        const template = FeeManagementTemplates.generateCollectAndReinvestTemplate(
            contractAddress,
            tokenId,
            minToken0,
            minToken1,
            estimatedGas
        );

        await createAndLaunchTemplate(template);
    }, [createAndLaunchTemplate]);

    const enableAutoCompoundWorkflow = useCallback(async (
        contractAddress: string,
        tokenId: string,
        minFeeThreshold: string,
        gasMultiplier: string,
        minInterval: string
    ) => {
        const template = FeeManagementTemplates.generateEnableAutoCompoundTemplate(
            contractAddress,
            tokenId,
            minFeeThreshold,
            gasMultiplier,
            minInterval
        );

        await createAndLaunchTemplate(template);
    }, [createAndLaunchTemplate]);

    // Template management
    const loadTemplates = useCallback(() => {
        return SmartTemplateBuilder.loadTemplates();
    }, []);

    const getTemplatesByCategory = useCallback((category: SmartTemplate['category']) => {
        return SmartTemplateBuilder.getTemplatesByCategory(category);
    }, []);

    const getTemplatesByContract = useCallback((contractName: string) => {
        return SmartTemplateBuilder.getTemplatesByContract(contractName);
    }, []);

    const deleteTemplate = useCallback((templateIndex: number) => {
        try {
            SmartTemplateBuilder.deleteTemplate(templateIndex);
            logger.log('Template deleted at index:', templateIndex);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete template';
            setError(errorMessage);
            logger.error('Error deleting template:', err);
        }
    }, []);

    const clearAllTemplates = useCallback(() => {
        try {
            SmartTemplateBuilder.clearAllTemplates();
            logger.log('All templates cleared');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to clear templates';
            setError(errorMessage);
            logger.error('Error clearing templates:', err);
        }
    }, []);

    return {
        // State
        isCreatingTemplate,
        error,

        // Core functions
        createTemplate,
        launchFunctionBuilder,
        createAndLaunchTemplate,

        // Fee Management
        createCollectAllFeesTemplate,
        createCollectAndReinvestTemplate,
        createEnableAutoCompoundTemplate,
        collectAllFeesWorkflow,
        collectAndReinvestWorkflow,
        enableAutoCompoundWorkflow,

        // Aerodrome
        createCollectPoolFeesTemplate,
        createCollectAndReinvestPoolTemplate,

        // Governance
        createProposalTemplate,

        // Treasury
        createTokenTransferTemplate,

        // Template management
        loadTemplates,
        getTemplatesByCategory,
        getTemplatesByContract,
        deleteTemplate,
        clearAllTemplates,

        // Clear error
        clearError: () => setError(null)
    };
};

export default useSmartTemplates;
